import { ChargeBeePlanId, PlanIdToNameMap, getSearchPerformanceMaxDomains } from '@prerender/billing-shared';

import { useCharbeePlanId, useIsSubscriptionCancelled } from '../../hooks/usePrerenderUser';

export type SearchPerformanceAccessUser = {
  chargebeePlanId: string;
  subscriptionCancelled: boolean;
};

export type SearchPerformanceAccessState = 'not-in-plan' | 'cancelled' | 'available';

const FALLBACK_PLAN_NAME = 'your plan';

export function getSearchPerformanceDomainCap({
  chargebeePlanId,
}: Pick<SearchPerformanceAccessUser, 'chargebeePlanId'>): number {
  return getSearchPerformanceMaxDomains(chargebeePlanId);
}

export function isSearchPerformanceCapUpgradable(cap: number): boolean {
  return cap > 0 && cap < getSearchPerformanceMaxDomains(ChargeBeePlanId.EnterprisePlus);
}

// Plan before cancellation: reactivating a plan that never included Search
// Performance would not grant it, so that account keeps the upgrade path.
export function getSearchPerformanceAccessState(user: SearchPerformanceAccessUser): SearchPerformanceAccessState {
  if (getSearchPerformanceDomainCap(user) === 0) {
    return 'not-in-plan';
  }
  if (user.subscriptionCancelled) {
    return 'cancelled';
  }
  return 'available';
}

export function hasSearchPerformanceAccess(user: SearchPerformanceAccessUser): boolean {
  return getSearchPerformanceAccessState(user) === 'available';
}

export function useSearchPerformanceDomainCap(): number {
  const chargebeePlanId = useCharbeePlanId();
  return getSearchPerformanceDomainCap({ chargebeePlanId });
}

export function getSearchPerformancePlanLabel({
  chargebeePlanId,
}: Pick<SearchPerformanceAccessUser, 'chargebeePlanId'>): string {
  return (PlanIdToNameMap as Partial<Record<string, string>>)[chargebeePlanId] ?? FALLBACK_PLAN_NAME;
}

export function formatSearchPerformanceDomainCap(used: number, max: number, planLabel: string): string {
  return `${used} of ${max} ${max === 1 ? 'domain' : 'domains'} · ${planLabel} plan`;
}

export function formatSearchPerformanceDomainCapHelp(max: number): string {
  return `You can sync up to ${max} ${
    max === 1 ? 'domain' : 'domains'
  } in total, across every connected Google account.`;
}

export function useSearchPerformancePlanLabel(): string {
  const chargebeePlanId = useCharbeePlanId();
  return getSearchPerformancePlanLabel({ chargebeePlanId });
}

export function useSearchPerformanceAccessState(): SearchPerformanceAccessState {
  const chargebeePlanId = useCharbeePlanId();
  const subscriptionCancelled = useIsSubscriptionCancelled();
  return getSearchPerformanceAccessState({ chargebeePlanId, subscriptionCancelled });
}

export function useHasSearchPerformanceAccess(): boolean {
  return useSearchPerformanceAccessState() === 'available';
}
